import React, { forwardRef, useCallback, useEffect, useImperativeHandle, useRef, useState } from "react";
import Button from "../common/Button";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { PulseLoader } from "react-spinners";
import AllocationTracker from "./components/AllocationTracker";
import CropReportItem from "./CropReportItem";
import { validateCrops } from "./useReportValidation";
import request from "../../utils/fetch";
import toast from "react-hot-toast";
import { Tooltip } from "react-tooltip";
import { LOCAL_PREFIX } from "../../utils/constants";
import { areCropsEqual } from "./utils/checkEquality";
import SectionFooter from "./SectionFooter";
import toISOString from "./utils/toISOString";
import useColorPool from "./useColorPool";
import ProblemIndicator from "./components/ProblemIndicator";

const CropReport = forwardRef(({
  tractAcres = 0,
  tractId,
  tractWaterNitrate,
  reportId,
  isDryLand,
  data,
  refreshData,
  reportYear,
  advanceLabel,
  advanceTab,
  initialProblems,
}, ref) => {
  const [saveStatus, setSaveStatus] = useState("clean");

  const colorPool = useColorPool();

  const localCropTracker = useRef(0);
  const [crops, setCrops] = useState(undefined);
  const dirtyCrops = useRef({});

  const [formProblems, setFormProblems] = useState(initialProblems);

  const [allocations, setAllocations] = useState([]);

  useEffect(() => {
    // Parse data into crops.
    const remoteCrops = data?.reduce((acc, c) => {
      acc[c._id] = {
        id: c._id,
        name: c.Crop_Planted,
        yield: c.Crop_Yield,
        yieldUnits: c.Crop_Yield_Units,
        yieldGoal: c.Yield_Goal ?? "",
        hasDamage: c.Crop_Damage ?? false,
        year: c.Year,
        irrigation: {
          sprinkler: c.Sprinkler_Acres ?? "",
          flood: c.Flood_Acres ?? "",
          other: c.Other_Irr_Acres ?? "",
        },
        irrigationApplied: c.Irrigation_Water_Applied ?? "",
        waterNitrate: c.Water_Nitrate ?? "",
        tillage: c.Tillage_Practice ?? "",
        note: c.Note ?? "",
        nitrogenApplications: c.Nitrogen_Applications?.map((n) => ({
          id: n._id,
          amount: n.amount,
          date: n.date_applied ? new Date(n.date_applied) : "",
          nitrogenApplied: n.direct_nitrogen || "",
          emergenceType: n.emergent_type,
          inhibitorType: n.inhibitor_type,
          inhibitorUsed: n.inhibitor_used,
          density: n.n_density || "",
          // TODO: Handle any weird floating point issues.
          potassium: n.k_percent * 100 || "",
          nitrogen: n.n_percent * 100 || "",
          phosphate: n.p_percent * 100 || "",
          type: n.type || "",
          units: n.units,
        })),
        manureApplications: c.Manure_Applications?.map((m) => ({
          id: m._id,
          ammoniumNitrogen: m.ammonium_amount,
          amount: m.amount,
          date: m.date_applied ? new Date(m.date_applied) : "",
          method: m.method,
          organicNitrogen: m.organic_amount,
          type: m.type,
        })),
      };

      return acc;
    }, {}) ?? {};

    // Update the crops without destroying local items or reordering anything.
    setCrops((og) => {
      og ??= {};
      const cropsToRemove = Object.keys(og).filter((c) => !c.startsWith(LOCAL_PREFIX));

      Object.keys(remoteCrops).forEach((key) => {
        // Don't overwrite any crops that haven't successfully saved.
        if (!dirtyCrops.current[key]) {
          // Maintain or retrieve a new color as needed.
          remoteCrops[key].color = og[key]?.color ?? colorPool.retrieve();
          og[key] = remoteCrops[key];
        }

        // Remove any known remote crop that has been deleted elsewhere, like if a different
        // user is editing this same report or if the user opened it in another window, etc.
        const index = cropsToRemove.indexOf(key);
        if (index >= 0) {
          cropsToRemove.splice(index, 1);
        }
      });

      cropsToRemove.forEach((k) => {
        colorPool.release(k.color);
        return delete og[k];
      });

      return { ...og };
    });
  }, [data]);

  const updateAllocations = useCallback((crops) => {
    setAllocations(Object.values(crops ?? {}).flatMap((item, i) => {
      if (item === "delete") return [];

      const { name, irrigation: ir } = item;
      return [{
        cropId: item.id,
        name: name || `Crop ${i + 1}`,
        color: item.color,
        value:
          Math.max(ir.sprinkler ?? 0, 0) + Math.max(ir.flood ?? 0, 0) + Math.max(ir.other ?? 0, 0),
      }];
    }));
  }, []);

  const checkDirty = useCallback((changed) => {
    if (!crops) return;

    const original = crops[changed.id];

    if (original && changed && areCropsEqual(original, changed)) {
      delete dirtyCrops.current[changed.id];

      if (Object.keys(dirtyCrops.current).length === 0) {
        setSaveStatus("clean");
      } else {
        setSaveStatus("dirty");
      }
    } else {
      dirtyCrops.current[changed.id] = changed;
      setSaveStatus("dirty");
      updateAllocations({ ...crops, ...dirtyCrops.current });
    }
  }, [crops]);

  useImperativeHandle(ref, () => ({ isDirty: saveStatus === "dirty" }), [saveStatus]);

  useEffect(() => {
    updateAllocations({ ...crops, ...dirtyCrops.current });
  }, [crops]);

  const handleSubmit = useCallback(() => {
    setSaveStatus("saving");

    const toSave = Object.keys(dirtyCrops.current).map((id) => {
      // NOTE: This is a little different from flowmeters because we store
      //       the whole dirty object in dirtyCrops instead of just a bool
      const exists = !id.startsWith(LOCAL_PREFIX);
      const crop = dirtyCrops.current[id];

      if (crop === "delete") return { id, deleted: true };

      const cropData = {
        _id: id,
        Crop_Planted: crop.name,
        Crop_Yield: crop.yield,
        Crop_Yield_Units: crop.yieldUnits,
        Crop_Damage: crop.hasDamage,
        Yield_Goal: crop.yieldGoal || 0,
        Year: crop.year || undefined,
        Sprinkler_Acres: crop.irrigation.sprinkler || 0,
        Flood_Acres: crop.irrigation.flood || 0,
        Other_Irr_Acres: crop.irrigation.other || 0,
        Irrigation_Water_Applied: crop.irrigationApplied || 0,
        Water_Nitrate: crop.waterNitrate || 0,
        Tillage_Practice: crop.tillage || undefined,
        Note: crop.note || undefined,
      };

      const nitrogenData = crop.nitrogenApplications?.map((n) => {
        if (n.deleted) return { deleted: true, _id: n.id };

        return {
          _id: n.id,
          amount: n.amount === "" ? undefined : n.amount,
          date_applied: n.date === "" ? "" : toISOString(n.date),
          direct_nitrogen: n.nitrogenApplied || undefined,
          emergent_type: n.emergenceType,
          inhibitor_type: n.inhibitorType,
          inhibitor_used: n.inhibitorUsed,
          n_density: n.density === "" ? undefined : n.density,
          k_percent: n.potassium === "" ? undefined : (n.potassium / 100),
          n_percent: n.nitrogen === "" ? undefined : (n.nitrogen / 100),
          p_percent: n.phosphate === "" ? undefined : (n.phosphate / 100),
          type: n.type,
          units: n.units,
        };
      });

      const manureData = crop.manureApplications?.map((m) => {
        if (m.deleted) return { deleted: true, _id: m.id };

        return {
          _id: m.id,
          date_applied: m.date === "" ? "" : toISOString(m.date),
          ammonium_amount: m.ammoniumNitrogen,
          amount: m.amount,
          method: m.method,
          organic_amount: m.organicNitrogen,
          type: m.type,
          // Calculate and append years_ago_applied from date.
          years_ago_applied: m.date !== "" ? (new Date()).getFullYear() - m.date.getFullYear() : 0,
        };
      });

      return { exists, id, cropData, nitrogenData, manureData };
    });

    const toValidate = Object.values(crops ?? {}).flatMap((s) => {
      const foundInDirty = toSave.find((t) => t.id === s.id);

      if (!foundInDirty) {
        const foundInRemote = data.find((d) => d._id === s.id)
        if (foundInRemote) return [foundInRemote];
        return [];
      }

      if (foundInDirty.deleted) return [];

      return [{
        ...foundInDirty.cropData,
        Nitrogen_Applications: foundInDirty.nitrogenData,
        Manure_Applications: foundInDirty.manureData,
      }];
    });

    const problems = validateCrops(toValidate, tractAcres);
    const stopSubmission = problems.some((v) => v.level === "error");
    setFormProblems(problems);

    if (stopSubmission) {
      setSaveStatus("dirty");
      return;
    }

    const subPromises = [];

    const cropPromises = toSave.map((d) => {
      if (d.deleted) {
        return request({
          method: "delete",
          url: `/reports/${reportId}/crops/${d.id}`,
        }).then(() => {
          delete crops[d.id];
          delete dirtyCrops.current[d.id];
        });
      }

      // Don't send local ids in the request, it hates when you do that.
      if (!d.exists) delete d.cropData._id;

      return request({
        method: d.exists ? "patch" : "post",
        data: d.cropData,
        url: `/reports/${reportId}/crops/${d.exists ? d.cropData._id : ""}`,
      }).then((result) => {
        // If this is an existing crop we already know the ID. Otherwise the result is the new ID.
        const remoteId = d.exists ? d.cropData._id : result;

        const upsertOrDeleteApp = (app, route) => {
          if (app.deleted) {
            return request({
              method: "delete",
              url: `/reports/${reportId}/crops/${remoteId}/${route}/${app._id}`
            });
          }

          const appExists = !app._id.startsWith(LOCAL_PREFIX);

          // We're going to need this id after it's deleted from the object.
          const appId = app._id;
          if (appExists) {
            let different = false;
            // Check that the application is actually different
            if (route === "nitrogen") {
              const ogCrop = data?.find((c) => c._id === d.id);
              const ogApp = ogCrop?.Nitrogen_Applications?.find((o) => o._id === app._id);
              if (ogApp) {
                // TODO: Also make sure percent floats compare correctly.
                different = ogApp.amount !== (app.amount ?? 0) ||
                  ogApp.date_applied !== app.date_applied ||
                  ogApp.direct_nitrogen !== app.direct_nitrogen ||
                  ogApp.emergent_type !== app.emergent_type ||
                  ogApp.inhibitor_type !== app.inhibitor_type ||
                  ogApp.inhibitor_used !== app.inhibitor_used ||
                  ogApp.n_density !== (app.n_density ?? 0) ||
                  ogApp.k_percent !== (app.k_percent ?? 0) ||
                  ogApp.n_percent !== (app.n_percent ?? 0) ||
                  ogApp.p_percent !== (app.p_percent ?? 0) ||
                  ogApp.type !== app.type;
              } else {
                different = true;
              }
            } else {
              const ogCrop = data?.find((c) => c._id === d.id);
              const ogApp = ogCrop?.Manure_Applications?.find((o) => o._id === app._id);
              if (ogApp) {
                different = ogApp.date_applied !== app.date_applied ||
                  ogApp.ammonium_amount !== app.ammonium_amount ||
                  ogApp.amount !== app.amount ||
                  ogApp.method !== app.method ||
                  ogApp.organic_amount !== app.organic_amount ||
                  ogApp.type !== app.type ||
                  ogApp.years_ago_applied !== app.years_ago_applied;
              } else {
                different = true;
              }
            }

            if (!different) {
              // This doesn't need to save, so just return an already resolved promise.
              return Promise.resolve();
            }
          } else {
            // Don't send local ids in the request, it hates when you do that.
            delete app._id;
          }

          return request({
            method: appExists ? "patch" : "post",
            data: app,
            url: `/reports/${reportId}/crops/${remoteId}/${route}/${appExists ? app._id : ""}`
          }).then((value) => {
            if (!appExists) {
              // Going from local to remote, so we need to track the new id.
              const crop = dirtyCrops.current[d.id];
              const apps = route === "nitrogen" ? crop.nitrogenApplications : crop.manureApplications;
              const replaceId = apps.find((a) => a.id === appId);
              replaceId.id = value;
            }
          });
        };

        const cropSubs = [
          ...d.nitrogenData?.map((app) => upsertOrDeleteApp(app, "nitrogen")),
          ...d.manureData?.map((app) => upsertOrDeleteApp(app, "manure")),
        ];

        subPromises.push(...cropSubs);

        Promise.all(cropSubs).then(() => {
          // This is saved now, so delete it from the dirty items.
          delete dirtyCrops.current[d.id];
        }).finally(() => {
          // If the crop had an local id before than we need to swap it out for the remote one.
          // The crop itself saved even if the applications didn't, so this happens either way.
          if (!d.exists) {
            crops[remoteId] = crops[d.id];
            delete crops[d.id];
          }
        });
      }).catch((e) => {
        // TODO: Make sure there's no edge cases where this doesn't work and requires a dispatch.
        crops[d.id].failedToSave = true;
        // Rethrow this error so the Promise.all still has something to catch.
        throw e;
      });
    });

    // Once all the crop promises resolve, we know the subPromises list is complete
    // so we can wait for those to complete, then set the saveStatus appropriately.
    Promise.all(cropPromises).then(() => {
      Promise.all(subPromises).then(() => {
        toast.success("Successfully updated crop report");
        refreshData().then(() => setSaveStatus("saved"));
      }).catch((e) => {
        toast.error(e.response?.data?.message ?? e.message ?? "Something went wrong");
        refreshData().then(() => setSaveStatus("dirty"));
      });
    }).catch((e) =>{
      console.error(e);
      toast.error(e.response?.data?.message ?? e.message ?? "Something went wrong");
      refreshData().then(() => setSaveStatus("dirty"));
    });
  }, [data, crops]);

  const addCrop = useCallback(() => {
    const id = `${LOCAL_PREFIX}-${localCropTracker.current}`;
    localCropTracker.current += 1;
    setCrops({
      ...crops,
      [id]: {
        id,
        color: colorPool.retrieve(),
        name: "",
        yield: "",
        yieldUnits: "",
        yieldGoal: "",
        year: reportYear,
        irrigation: {
          sprinkler: "",
          flood: "",
          other: "",
        },
        irrigationApplied: "",
        waterNitrate: tractWaterNitrate ?? "",
        tillage : "",
        nitrogenApplications: [],
        manureApplications: [],
      }
    });
  }, [crops, allocations]);

  // If tractWaterNitrate changes, update any local entries that have no value
  useEffect(() => {
    if (!tractWaterNitrate || !crops || saveStatus === "saving") return;

    Object.values(crops).forEach((c) => {
      if (c.id.startsWith(LOCAL_PREFIX) && c.waterNitrate === "") {
        c.waterNitrate = tractWaterNitrate;
      }
    });

    setCrops({ ...crops });
  }, [tractWaterNitrate]);

  const nonFieldProblems = formProblems.filter((p) => !p.field && !p.hidden);
  const hasFieldErrors = formProblems.some((p) => {
    return p.field && (p.level === "error" || p.displayLevel === "error")
  });

  return (<>
    <div className="w-full flex justify-between">
      <h3 className="text-2xl font-semibold">Crops</h3>
      {crops && (
        <Button className="h-7" onClick={addCrop}>+ Add Crop</Button>
      )}
    </div>
    <div className="w-full">
      {!crops && <PulseLoader className="flex p-16 self-center justify-center" color="#658245" />}
      {crops && <AllocationTracker
        allocations={allocations}
        total={tractAcres}
        units="ac"
        overflowWarning="Acreage is over-allocated"
        showAllocation
        showTotal
        allocationTrailing
      />}
      <div
        className="overflow-y-auto pt-2 my-2 flex flex-col gap-1"
        style={{
          // Roughly the height that keeps the save button in view on initial page load.
          minHeight: "calc(100lvh - 40rem)",
          // Roughly the height that keeps the save button and tabs in view when scrolled past the map.
          maxHeight: "calc(100lvh - 20rem)",
        }}
      >
        {Object.values(crops ?? {})?.map((crop, index) => {
          return <CropReportItem
            key={`crop-item-${crop.id}}`}
            formProblems={formProblems}
            setFormProblems={setFormProblems}
            index={index}
            crop={crop}
            isDryLand={isDryLand}
            checkDirty={checkDirty}
            onRemove={() => {
              // Remove any problems associated with this crop.
              setFormProblems(formProblems.filter((p) => !p.field?.startsWith(crop.id) && !p.id?.startsWith(crop.id)));

              setCrops((c) => {
                colorPool.release(c.color);
                delete c[crop.id];

                if (crop.id.startsWith(LOCAL_PREFIX)) {
                  // This was never saved, so just remove it from the list and we're good.
                  delete dirtyCrops.current[crop.id];
                } else {
                  // This has been saved, so we need to delete it the next time we save.
                  dirtyCrops.current[crop.id] = "delete";
                }

                if (Object.keys(dirtyCrops.current).length === 0) {
                  setSaveStatus("clean");
                } else {
                  setSaveStatus("dirty");
                }

                return { ...c };
              });
            }}
          />;
        })}
        {(!crops || Object.values(crops ?? {}).length === 0) && <div className="self-center py-4">
          <div className="mb-4">No crops in report</div>
          <Button className="text-lg" onClick={addCrop}>Add Crop</Button>
        </div>}
      </div>
    </div>
    {nonFieldProblems.map((p, i) => (
      <ProblemIndicator
        key={`crop_form_issue_${i}`}
        level={p.displayLevel ?? p.level}
        message={p.message}
      />
    ))}
    {hasFieldErrors && <div
      key="crop_form_has_field_errors"
      className="text-sm flex gap-0.5 items-center pl-0.5 text-red-600"
    >
      <FontAwesomeIcon icon="circle-exclamation" />
      One or more fields have errors
    </div>}
    <SectionFooter
      status={saveStatus}
      handleSubmit={handleSubmit}
      advanceTab={advanceTab}
      advanceLabel={advanceLabel}
    />
    <Tooltip id="unsaved-crop-tooltip" place="right">
      An error occurred while saving this crop, please check it for issues and try again.
    </Tooltip>
    <Tooltip id="invalid-crop-tooltip" place="right">
      There are one or more issues with this crop that must be addressed before saving.
    </Tooltip>
    <Tooltip id="invalid-application-tooltip" place="right">
      There are one or more issues with this application that must be addressed before saving.
    </Tooltip>
  </>);
});

export default CropReport;
